import { useState } from 'react'
import type { GrantableScope } from '@/api/pending'
import { ApiError } from '@/api/errors'
import {
  Button,
  Caution,
  Eyebrow,
  Field,
  Icon,
  Modal,
  Pill,
  Segmented,
  SummaryStrip,
  useToast,
} from '@/ui'
import { useGrantScope } from './api'
import { S, SCOPE_LABEL, SCOPE_POWERS } from './strings'
import styles from './StaffView.module.css'

interface GrantModalProps {
  condoId: number
  onClose: () => void
}

export function GrantModal({ condoId, onClose }: GrantModalProps) {
  const toast = useToast()
  const grant = useGrantScope(condoId)
  const [step, setStep] = useState<'form' | 'confirm'>('form')
  const [email, setEmail] = useState('')
  const [scope, setScope] = useState<GrantableScope>('reviewer')
  const [ack, setAck] = useState(false)
  const address = email.trim()
  const tone = scope === 'admin' ? 'brand' : 'neutral'

  function confirm() {
    grant.mutate(
      { email: address, scope },
      {
        onSuccess: () => {
          toast(S.granted)
          onClose()
        },
      },
    )
  }

  if (step === 'form') {
    return (
      <Modal
        title={S.grantTitle}
        onClose={onClose}
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>
              {S.cancel}
            </Button>
            <Button
              disabled={!address.includes('@')}
              onClick={() => {
                setAck(false)
                grant.reset()
                setStep('confirm')
              }}
            >
              {S.reviewAndConfirm}
            </Button>
          </>
        }
      >
        <Field
          label={S.email}
          value={email}
          onChange={setEmail}
          placeholder={S.emailPlaceholder}
          hint={S.emailHint}
          type="email"
          autoFocus
        />
        <div>
          <Eyebrow>{S.scope}</Eyebrow>
          <div className={styles.segmentedWrap}>
            <Segmented<GrantableScope>
              aria-label={S.scope}
              value={scope}
              onChange={setScope}
              items={[
                { id: 'reviewer', label: SCOPE_LABEL.reviewer },
                { id: 'doorman', label: SCOPE_LABEL.doorman },
                { id: 'admin', label: SCOPE_LABEL.admin },
              ]}
            />
          </div>
        </div>
      </Modal>
    )
  }

  return (
    <Modal
      width={560}
      title={S.confirmTitle}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" icon="chevron_left" onClick={() => setStep('form')}>
            {S.back}
          </Button>
          <Button icon="key" disabled={!ack} loading={grant.isPending} onClick={confirm}>
            {S.confirmGrant}
          </Button>
        </>
      }
    >
      <SummaryStrip
        name={address.split('@')[0]!.replace(/[._]/g, ' ')}
        title={address}
        subtitle={S.willReceive(SCOPE_LABEL[scope])}
        aside={<Pill tone={tone}>{SCOPE_LABEL[scope]}</Pill>}
      />
      <div className={styles.powers}>
        <Eyebrow>{S.canDo}</Eyebrow>
        {SCOPE_POWERS[scope].map((p) => (
          <div key={p} className={styles.power}>
            <Icon name="check" size={16} color="var(--color-primary)" />
            <span>{p}</span>
          </div>
        ))}
      </div>
      {scope === 'admin' && <Caution icon="shield">{S.adminCaution}</Caution>}
      <label className={styles.ack}>
        <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
        <span>{S.ack}</span>
      </label>
      {grant.isError && (
        <p role="alert" className={styles.error}>
          {grant.error instanceof ApiError && grant.error.status === 404
            ? S.grantNotFound
            : S.grantError}
        </p>
      )}
    </Modal>
  )
}
